import React, { Fragment, useEffect, useRef, useState, useMemo } from "react";
import { Row, Table } from "reactstrap";
import Select from "react-select";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { DebouncedInput } from "helpers/common_helper";
import Pagination from "components/Common/Pagination";  
import axiosInstance from "pages/Utility/axiosInstance";
import { useDispatch } from "react-redux";
import { fetchLessonsRequest } from "store/actions";
import { FadeLoader } from "react-spinners";

const LessonDataTable = ({
  columns,
  data,
  tableClass,
  isPagination = true,
  isGlobalFilter,
  SearchPlaceholder,
  totalrows,
  loading,
  selectedSortData,
  setSelectedSortData,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  searchString,
  setSearchString,
  courseOptions,
  selectedCourse,
  setSelectedCourse,
  selectedModule,
  setSelectedModule,
  selectedChapter,
  setSelectedChapter,
  creatorOptions,
  selectedCreator,
  setSelectedCreator,
}) => {
  const tableRef = useRef();
  const dispatch = useDispatch();

  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);

  // ────────────────────────────────
  // Fetch Modules & Chapters
  // ────────────────────────────────
  const fetchModulesByCourse = async (courseId) => {
    if (!courseId) return setModules([]);
    try {
      const res = await axiosInstance.get(`V1/modules/dropdown/${courseId}`);
      setModules(res?.data?.data || []);
    } catch {
      setModules([]);
    }
  };

  const fetchChaptersByModule = async (moduleId) => {
    if (!moduleId) return setChapters([]);
    try {
      const res = await axiosInstance.get(`V1/chapters/by-module/${moduleId}`);
      setChapters(res?.data?.data || []);
    } catch {
      setChapters([]);
    }
  };

  useEffect(() => {
    if (selectedCourse) {
      fetchModulesByCourse(selectedCourse.value);
      setSelectedModule(null);
      setSelectedChapter(null);
      setChapters([]);
    } else {
      setModules([]);
      setSelectedModule(null);
      setSelectedChapter(null);
      setChapters([]);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedModule) {
      fetchChaptersByModule(selectedModule.value);
      setSelectedChapter(null);
    } else {
      setChapters([]);
      setSelectedChapter(null);
    }
  }, [selectedModule]);

  // ────────────────────────────────
  // Fetch Lessons (with all filters)
  // ────────────────────────────────
  useEffect(() => {
    if (!selectedChapter?.value) return;

    const params = {
      page: pageIndex,
      limit: pageSize,
      chapterId: selectedChapter.value,
    };

    if (selectedCreator?.value) params.createdBy = selectedCreator.value;
    if (selectedSortData?.value && selectedSortData?.direction)
      params.sortBy = `${selectedSortData.value}:${selectedSortData.direction}`;
    if (searchString) params.search = searchString;

    console.log("FETCH LESSONS PARAMS ===>", params);
    dispatch(fetchLessonsRequest(params));
  }, [
    dispatch,
    pageIndex,
    pageSize,
    selectedChapter,
    selectedCreator,
    selectedSortData,
    searchString,
  ]);

  useEffect(() => {
    setPageIndex(1);
  }, [
    searchString,
    selectedCourse,
    selectedModule,
    selectedChapter,
    selectedCreator,
  ]);

  // ────────────────────────────────
  // Columns & Table Setup
  // ────────────────────────────────
  const serialColumn = useMemo(
    () => ({
      header: "#",
      id: "serial",
      cell: ({ row }) => (pageIndex - 1) * pageSize + row.index + 1,
      enableSorting: false,
    }),
    [pageIndex, pageSize]
  );

  const finalColumns = useMemo(
    () => [serialColumn, ...columns],
    [columns, serialColumn]
  );

  const table = useReactTable({
    columns: finalColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const totalPages = Math.ceil(totalrows / pageSize);
  const handlePageChange = (p) => setPageIndex(p);

  // ────────────────────────────────
  // UI
  // ────────────────────────────────
  return (
    <Fragment>
      {/* Filters */}
      <Row className="mb-2">
        {isGlobalFilter && (
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div style={{ width: "220px" }}>
              <Select
                options={courseOptions}
                value={selectedCourse}
                onChange={setSelectedCourse}
                placeholder="Filter by Course"
                isClearable
              />
            </div>

            <div style={{ width: "220px" }}>
              <Select
                options={modules.map((m) => ({
                  label: m.title,
                  value: m._id,
                }))}
                value={selectedModule}
                onChange={setSelectedModule}
                placeholder="Filter by Module"
                isClearable
              />
            </div>

            <div style={{ width: "220px" }}>
              <Select
                options={chapters.map((c) => ({
                  label: c.title,
                  value: c._id,
                }))}
                value={selectedChapter}
                onChange={setSelectedChapter}
                placeholder="Filter by Chapter"
                isClearable
              />
            </div>

            <div style={{ width: "220px" }}>
              <Select
                options={creatorOptions}
                value={selectedCreator}
                onChange={setSelectedCreator}
                placeholder="Filter by Creator"
                isClearable
              />
            </div>

            <div style={{ flex: 1, minWidth: "200px", maxWidth: "250px" }}>
              <DebouncedInput
                value={searchString ?? ""}
                onChange={(value) => setSearchString(String(value))}
                className="form-control"
                placeholder={SearchPlaceholder || "Search..."}
              />
            </div>
          </div>
        )}
      </Row>

      {/* Table */}
      <div className="table-responsive" ref={tableRef}>
        <Table hover className={tableClass} bordered>
<thead>
  {table.getHeaderGroups().map((headerGroup) => (
    <tr key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <th
          key={header.id}
          style={{
            verticalAlign: "middle",
            whiteSpace: "nowrap", // keep headers in single line
            textAlign: "left",
            maxWidth:
              header.column.id === "createdBy"
                ? "140px"
                : header.column.id === "title"
                ? "220px"
                : "150px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={
            typeof header.column.columnDef.header === "string"
              ? header.column.columnDef.header
              : ""
          }
        >
          <div className="d-flex align-items-center justify-content-between">
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {flexRender(
                header.column.columnDef.header,
                header.getContext()
              )}
            </span>

            {/* Sorting icons */}
            {header.column.columnDef.accessorKey &&
              !["serial", "actions"].includes(header.column.id) && (
                <span
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const field = header.column.columnDef.accessorKey;
                    if (!selectedSortData || selectedSortData.value !== field)
                      setSelectedSortData({ value: field, direction: "asc" });
                    else if (selectedSortData.direction === "asc")
                      setSelectedSortData({ value: field, direction: "desc" });
                    else
                      setSelectedSortData({ value: field, direction: "asc" });
                  }}
                >
                  {selectedSortData?.value ===
                  header.column.columnDef.accessorKey ? (
                    selectedSortData.direction === "asc" ? (
                      <i className="mdi mdi-arrow-up" style={{ fontSize: 18 }}></i>
                    ) : (
                      <i className="mdi mdi-arrow-down" style={{ fontSize: 18 }}></i>
                    )
                  ) : (
                    <i
                      className="mdi mdi-swap-vertical"
                      style={{ fontSize: 18 }}
                    ></i>
                  )}
                </span>
              )}
          </div>
        </th>
      ))}
    </tr>
  ))}
</thead>


          <tbody>
            {loading ? (
              <tr>
                <td colSpan={finalColumns.length} className="text-center">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <FadeLoader color="#00a895" size={40} />
                  </div>
                </td>
              </tr>
            ) : data?.length === 0 ? (
              <tr>
                <td colSpan={finalColumns.length} className="text-center">
                  No Data Found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ verticalAlign: "middle" }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && totalrows > 0 && (
        <Pagination
          currentPage={pageIndex}
          totalPages={totalPages}
          totalItems={totalrows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          showInfo
        />
      )}
    </Fragment>
  );
};

export default LessonDataTable;



